import { ref, Ref } from "vue";
import { AGENT_TRACKER_ROLE, ONSITE_TYPE } from "vue-app/types/enums";
import { DTOOnsiteIndicator } from "vue-app/api/dto";
import { isAIAgentAvailable, aiAgentMenuItem } from "vue-app-new/plugins/AIAgent/AIAgent.plugin";

export type MenuItem = {
  name: string;
  label?: string;
  path?: string;
  disabled?: boolean;
  iconName?: string;
  hidden?: boolean;
  children?: MenuItem[];
  subpages?: string[];
  newFeatureRangeDate?: { start: string; end: string };
};

const userRoles = Object.keys(window.EDRONE_CONTEXT.user.roles).filter((key) => window.EDRONE_CONTEXT.user.roles[key]);
const isSuspended =
  (window.EDRONE_CONTEXT.tracker?.lifecycle?.isSuspended || false) && !window.EDRONE_CONTEXT.user.isDev;

export const mainTree: Ref<MenuItem[]> = ref([
  {
    name: "live-events",
    label: window._trans._liveEvents.title,
    path: "/app/live-events",
    disabled: isSuspended,
    iconName: "rec-live-events",
    children: [],
  },
  {
    name: "inbox",
    label: window._trans._menu.item.inbox,
    path: "/app/inbox",
    disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_INBOX),
    iconName: "fa-light fa-inbox",
    children: [],
  },
  {
    name: "campaigns",
    label: window._trans._menu.item.campaigns,
    path: "",
    disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT),
    iconName: "fa-light fa-envelopes",
    children: [
      {
        name: "newsletter-hub",
        label: window._trans._menu.item.newsletters,
        path: "/app/newsletters/email",
        disabled: isSuspended,
        children: [],
        subpages: ["email", "editor", "templates-editor"],
      },
      {
        name: "newsletters-sms",
        label: window._trans._menu.item.newslettersSMS,
        newFeatureRangeDate: { start: "2025-05-26", end: "2025-05-31" },
        path: "/app/newsletters/sms",
        disabled: isSuspended,
        children: [],
        subpages: ["sms", "sms-editor"],
      },
      {
        name: "newsletters-whatsapp",
        label: window._trans._menu.item.newslettersWhatsapp,
        newFeatureRangeDate: { start: "2025-07-01", end: "2025-08-31" },
        path: "/app/newsletters/whatsapp",
        disabled: isSuspended,
        children: [],
        subpages: ["whatsapp", "whatsapp-editor"],
      },
      {
        name: "newsletters/coupons",
        label: window._trans._menu.item.coupons,
        path: "/app/newsletters/coupons",
        disabled: isSuspended,
        children: [],
      },
    ],
  },
  {
    name: "automations",
    label: window._trans._menu.item.automations,
    path: "",
    disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT),
    iconName: "fa-light fa-split",
    children: [
      {
        name: "engagements",
        label: window._trans._menu.item.engagements,
        path: "/app/ready-flows",
        disabled: isSuspended,
        children: [],
        subpages: [
          "restore-customers",
          "loyalty-programme",
          "recover-carts",
          "advanced-restore-customers",
          "after-sale",
          "newsletter-subscription",
          "wish-list",
          "recommendations",
          "cross-selling-transactional",
          "chat-subscription",
          "birthday",
          "refer-a-friend",
          "sms-newsletter",
          "newsletter-subscription-v2",
          "newsletter-signup-after-purchase",
          "price-change-alert-up",
          "price-change-alert-down",
          "newest-products",
          "low-stock-products",
          "ask-for-review",
          "back-in-stock",
        ],
      },
      {
        name: "custom-flows",
        label: window._trans._menu.item.customFlows,
        path: "/app/custom-flows",
        disabled: isSuspended,
        children: [],
        subpages: [
          "cf-dashboard",
          "custom-flow",
          "design-emails",
          "design-email",
          "design-popup",
          "design-popups",
          "design-sms",
          "design-whatsapp",
        ],
      },
      {
        name: "coupons",
        label: window._trans._menu.item.coupons,
        path: "/app/custom-flows/coupons",
        disabled: isSuspended,
        children: [],
      },
    ],
  },
  {
    name: "onsites",
    label: window._trans._menu.item.onsites,
    path: "",
    disabled:
      isSuspended ||
      (!hasAccess(AGENT_TRACKER_ROLE.ROLE_ONSITE) && isSuspended) ||
      (!hasAccess(AGENT_TRACKER_ROLE.ROLE_INBOX) && isSuspended) ||
      !hasAccess(AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT),
    iconName: "fa-light fa-window-restore",
    newFeatureRangeDate: { start: "2025-04-16", end: "2025-04-30" },
    children: [
      {
        name: "cross-sell",
        label: window._trans._menu.item.smartCrossSelling,
        path: "/app/onsite/smart-recommendations",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ONSITE),
        children: [],
        hidden: false,
        newFeatureRangeDate: { start: "2025-04-16", end: "2025-04-30" },
        subpages: ["edit"],
      },
      {
        name: "popup",
        label: window._trans._menu.item.popup,
        path: "/app/onsite/popups",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ONSITE),
        children: [],
        subpages: ["popups"],
      },
      {
        name: "slider",
        label: window._trans._menu.item.slider,
        path: "/app/onsite/sliders",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ONSITE),
        children: [],
        subpages: ["sliders"],
      },
      {
        name: "banner",
        label: window._trans._menu.item.banner,
        path: "/app/onsite/banners",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ONSITE),
        children: [],
        subpages: ["banners"],
      },
      {
        name: "social-proof",
        label: window._trans._menu.item.socialProof,
        path: "/app/onsite/social-proofs",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ONSITE),
        children: [],
        subpages: ["social-proofs"],
      },
      {
        name: "whatsapp-widget",
        label: window._trans._menu.item.whatsappWidget,
        path: "/app/onsite/whatsapp-widget",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ONSITE),
        newFeatureRangeDate: { start: "2025-10-01", end: "2025-10-31" },
        children: [],
      },
      ...(isAIAgentAvailable()
        ? [
            {
              ...aiAgentMenuItem,
              disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ONSITE),
            },
          ]
        : []),
      {
        name: "chat",
        label: window._trans._menu.item.chat,
        path: "/app/onsite/chat",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_INBOX),
        children: [],
      },
      {
        name: "push-newsletter",
        label: window._trans._menu.item.pushNewsletter,
        path: "/app/onsite/push-newsletter",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ENGAGEMENT),
        children: [],
        subpages: ["push-newsletter"],
      },
      {
        name: "marketing-machine",
        label: window._trans._menu.item.marketingMachine,
        path: "/app/onsite/mm",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_ONSITE),
        hidden: true, // !window.EDRONE_CONTEXT?.tracker.configuration.isMarketingMachineEnabled
        children: [],
      },
    ],
  },
  {
    name: "customers",
    label: window._trans._menu.item.customers,
    path: "",
    disabled:
      isSuspended ||
      (!hasAccess(AGENT_TRACKER_ROLE.ROLE_CRM) &&
        !hasAccess(AGENT_TRACKER_ROLE.ROLE_DB) &&
        !hasAccess(AGENT_TRACKER_ROLE.ROLE_INBOX)),
    iconName: "fa-light fa-users-rectangle",
    children: [
      {
        name: "contacts",
        label: window._trans._menu.item.contacts,
        path: "/app/segmentation/engagement",
        disabled: isSuspended,
        hidden: !window.EDRONE_CONTEXT?.tracker.configuration.isSegmentationEnabled,
        children: [],
        subpages: ["segments"],
      },
      {
        name: "segments",
        label: window._trans._menu.item.segments || "Segments",
        path: "/app/segmentation/segments",
        disabled: isSuspended,
        hidden: !window.EDRONE_CONTEXT?.tracker.configuration.isSegmentationEnabled,
        children: [],
      },
      {
        name: "tags",
        label: window._trans._menu.item.tags,
        path: "/app/tags",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_CRM),
        children: [],
      },
      {
        name: "customers",
        label: window._trans._menu.item.customers,
        path: "/app/customers",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_CRM),
        children: [],
      },
      {
        name: "rfm",
        label: window._trans._menu.item.rfm,
        path: "/app/rfm-analysis",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_CRM),
        children: [],
        subpages: ["rfm-overview", "rfm-breakdown", "rfm-settings"],
      },
      {
        name: "import",
        label: window._trans._menu.item.import,
        path: "/app/import",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_DB),
        children: [],
        subpages: ["v2"],
      },
      {
        name: "export",
        label: window._trans._menu.item.export,
        path: "/app/export",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_DB),
        children: [],
      },
    ],
  },
  {
    name: "product-categories",
    label: window._trans._menu.item.products,
    path: "/app/product-categories",
    disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_CRM),
    hidden: false,
    iconName: "fa-light fa-box-open",
    newFeatureRangeDate: { start: "2025-04-29", end: "2025-05-15" },
    children: [],
  },
  {
    name: "orders",
    label: window._trans._menu.item.orders,
    path: "/app/orders",
    disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_CRM),
    hidden: false,
    iconName: "fa-light fa-cart-shopping",
    newFeatureRangeDate: { start: "2025-06-12", end: "2025-06-26" },
    children: [],
  },
  {
    name: "reports",
    label: window._trans._menu.item.reports,
    path: "",
    disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_REPORTS),
    iconName: "fa-light fa-chart-line-up",
    children: [
      {
        name: "general",
        label: window._trans._menu.item.general,
        path: "/app/dashboard/general",
        disabled: isSuspended,
        children: [],
      },
      {
        name: "automations",
        label: window._trans._menu.item.newsletters,
        path: "/app/dashboard/engagements/email",
        disabled: isSuspended,
        children: [],
      },
      {
        name: "automations",
        label: window._trans._menu.item.newslettersSMS,
        path: "/app/dashboard/engagements/sms",
        disabled: isSuspended,
        children: [],
      },
      {
        name: "automations",
        label: window._trans._menu.item.newslettersWhatsapp,
        path: "/app/dashboard/engagements/whatsapp",
        disabled: isSuspended,
        children: [],
      },
      {
        name: "crm",
        label: window._trans._menu.item.crm,
        path: "/app/crm",
        disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_CRM),
        children: [],
      },
      {
        name: "onsites",
        label: window._trans._menu.item.onsites,
        path: "/app/dashboard/onsite",
        disabled: isSuspended,
        children: [],
      },
      {
        name: "inbox-dashboard",
        label: window._trans._menu.item.inbox,
        path: "/app/dashboard/inbox",
        disabled: isSuspended,
        children: [],
      },
    ],
  },
]);

export const settingsTree: Ref<MenuItem[]> = ref(
  [
    {
      name: "manage",
      label: window._trans._menu.item.account,
      disabled: isSuspended,
      path: "/app/account",
      iconName: "fa-light fa-user",
      children: [],
    },
    {
      name: "agents",
      label: window._trans._menu.item.agents,
      path: "/app/settings/agents",
      disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_SETTINGS),
      iconName: "fa-light fa-user-plus",
      children: [],
    },
    {
      name: "integrations",
      label: window._trans._menu.item.integrations,
      path: "/app/integrations",
      disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_SETTINGS),
      iconName: "fa-light fa-plug",
      children: [],
    },
    {
      name: "sending-domain",
      label: window._trans._menu.item.sendingDomain,
      path: "/app/settings/sending-domain",
      disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_SETTINGS),
      iconName: "fa-light fa-at",
      children: [],
    },
    {
      name: "products",
      label: window._trans._menu.item.productFeed,
      path: "/app/products",
      disabled: isSuspended || !hasAccess(AGENT_TRACKER_ROLE.ROLE_SETTINGS),
      hidden: !window.EDRONE_CONTEXT?.hasAccessToFeed,
      iconName: "fa-light fa-tag",
      children: [],
    },
    {
      name: "branding",
      label: window._trans._menu.item.branding,
      path: "/app/settings/branding",
      iconName: "fa-light fa-boxes-packing",
      children: [],
    },
    {
      name: "workspace",
    },
    {
      name: "logout",
      label: window._trans._menu.item.logout,
      path: "/logout",
      disabled: isSuspended,
      iconName: "fa-light fa-arrow-right-from-bracket",
      children: [],
    },
  ].filter((item) => {
    if (item.name === "products") {
      return window.EDRONE_CONTEXT?.hasAccessToFeed;
    }
    return true;
  })
);

export const integrationsTree: Ref<MenuItem[]> = ref([
  {
    name: "platforms",
    label: window._trans._menu.item.platforms,
    path: "/app/integrations",
    disabled: isSuspended,
    children: [],
  },
  {
    name: "whatsapp",
    label: window._trans._menu.item.whatsapp,
    path: "/app/integrations/whatsapp",
    disabled: isSuspended,
    children: [],
  },
  {
    name: "facebook-messenger",
    label: window._trans._menu.item.messenger,
    path: "/app/integrations/messenger",
    disabled: isSuspended,
    children: [],
  },
  {
    name: "google-analytics",
    label: "Google Analytics",
    path: "/app/integrations/google-analytics",
    disabled: isSuspended,
    children: [],
  },
  {
    name: "landing-pages",
    label: window._trans._menu.item.landingPages,
    path: "/app/integrations/landing-pages",
    disabled: isSuspended,
    children: [],
  },
  {
    name: "allegro",
    label: "Allegro",
    path: "/app/integrations/allegro",
    disabled: isSuspended,
    children: [],
  },
]);

export const customFlowsTree: Ref<MenuItem[]> = ref([
  {
    name: "dashboard",
    label: window._trans._menu.item.customFlowsDashboard,
    path: "/app/custom-flows/cf-dashboard",
  },
  {
    name: "email-designs",
    label: window._trans._menu.item.customFlowsEmails,
    path: "/app/custom-flows/design-emails",
  },
  {
    name: "email-sms",
    label: window._trans._menu.item.customFlowsSms,
    path: "/app/custom-flows/design-sms",
  },
  {
    name: "popup-designs",
    label: window._trans._menu.item.customFlowsPopups,
    path: "/app/custom-flows/design-popups",
  },
  {
    name: "design-whatsapp",
    label: window._trans._menu.item.customFlowsWhatsapp,
    path: "/app/custom-flows/design-whatsapp",
    newFeatureRangeDate: { start: "2025-07-01", end: "2025-08-31" },
  },
]);

function hasAccess(requiredRole: string): boolean {
  return userRoles.some((role) => {
    return role === requiredRole;
  });
}

export function disableItemsForSuspended(items: MenuItem[]) {
  if (!items || !items.length) {
    return;
  }
  items.forEach((item) => {
    if (item.children) {
      disableItemsForSuspended(item.children);
    }
    if (
      isSuspended &&
      item.path !== "/app/dashboard" &&
      item.path !== "/app/settings/manage" &&
      item.path !== "/app/account/billing"
    ) {
      item.disabled = true;
    }
  });
}

export function findNestedMenuItem(menu: MenuItem[] | undefined, name: string): MenuItem | undefined {
  if (!name || !menu) return undefined;
  for (let i = 0; i < menu.length; i++) {
    if (menu[i].name === name) {
      return menu[i];
    } else if (menu[i].children?.length) {
      const result = findNestedMenuItem(menu[i].children, name);
      if (result) {
        return result;
      }
    }
  }
  return undefined;
}

export function isActive(routePath: string, item: MenuItem): boolean {
  return (
    routePath === item.path ||
    (!!item.subpages?.length &&
      routePath.split("/").some((page) => item.subpages?.includes(page)) &&
      routePath.includes(item.path || ""))
  );
}

export function getBadgeText(name: string, data: any): string | undefined {
  if (!data) {
    return undefined;
  }

  const itemsWithBadge: Record<string, () => string | undefined | DTOOnsiteIndicator> = {
    inbox: () => data.unreadInboxConversations?.toString(),
    engagements: () => `${data.activeReadyFlows ?? 0} / 17`,
    "cross-sell": () => data.onsiteActivity[ONSITE_TYPE.SMART_CROSS_SELLING] ?? "NONE",
    popup: () => data.onsiteActivity[ONSITE_TYPE.POPUP_V2] ?? "NONE",
    slider: () => data.onsiteActivity[ONSITE_TYPE.SLIDER] ?? "NONE",
    banner: () => data.onsiteActivity[ONSITE_TYPE.BANNER] ?? "NONE",
    "social-proof": () => data.onsiteActivity[ONSITE_TYPE.SOCIAL_PROOF] ?? "NONE",
    "whatsapp-widget": () => data.onsiteActivity[ONSITE_TYPE.WHATSAPP_WIDGET] ?? "NONE",
    "ai-agent-widget": () => data.onsiteActivity[ONSITE_TYPE.AI_AGENT] ?? "NONE",
    chat: () => (data.chatEnabled ? "1" : "0"),
    segments: () => {
      return data.segmentsCount?.toString();
    },
    "custom-flows": () => {
      if (data.customFlowsCount === 0) {
        return "";
      }
      return `${data.activeCustomFlowsCount ?? 0} / ${data.customFlowsCount}`;
    },
  };

  const badgeText = itemsWithBadge[name];
  return badgeText ? badgeText() : undefined;
}

export function checkNestedChildren(children: MenuItem[], currentPath: string): boolean {
  return children.some(
    (child) =>
      isActive(currentPath, child) || (child.children?.length && checkNestedChildren(child.children, currentPath))
  );
}
